import { MouseEvent, useCallback, useEffect, useState } from 'react';

import Button, { ButtonType } from 'inquiry/components/Button';
import { useApply } from 'inquiry/lib/jsonLogic';
import { ButtonCompleteComponent } from 'lib/interfaces/ui-components';
import { flowHasCompleteAction } from 'verify/lib/inquiry';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';

interface ButtonCompleteProps {
  component: ButtonCompleteComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
  inquiryLoading: boolean;
}

const ButtonComplete = ({
  component,
  inquiry,
  actionsApi,
  inquiryLoading,
}: ButtonCompleteProps) => {
  const {
    disabled,
    hidden,
    text,
    'auto-submit-countdown-text': autoSubmitCountdownText,
    'auto-submit-interval-seconds': autoSubmitIntervalSeconds,
  } = component.attributes;

  const { onComplete } = actionsApi;

  const handleComplete = useCallback(
    (e?: MouseEvent<HTMLButtonElement>): void => {
      e?.preventDefault();
      onComplete();
    },
    [onComplete],
  );

  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(autoSubmitIntervalSeconds);

  useEffect(() => {
    if (autoSubmitCountdown != null && handleComplete != null) {
      if (autoSubmitCountdown > 0) {
        const timeout = setTimeout(() => {
          if (autoSubmitCountdown > 1) {
            setAutoSubmitCountdown(autoSubmitCountdown - 1);
          } else {
            setAutoSubmitCountdown(autoSubmitCountdown - 1);
            handleComplete();
          }
        }, 1000);

        return () => clearTimeout(timeout);
      }
    }
  }, [autoSubmitCountdown, handleComplete]);

  const apply = useApply(component.name, inquiry.attributes['next-step'].name);
  const isHidden = apply<boolean>(hidden);

  if (isHidden || !flowHasCompleteAction(inquiry)) {
    return null;
  }

  // Note: it's best practice to ensure the component that disables the button
  // is required. So if a user manually submits via devtools or something, the
  // transition will still fail. Without this, the button disable is a purely
  // UI level thing and can be trivially bypassed.
  const isDisabled = inquiryLoading || apply<boolean>(disabled);

  const buttonText =
    autoSubmitCountdown != null && autoSubmitCountdown > 0 && !!autoSubmitCountdownText
      ? autoSubmitCountdownText.replace('{time}', String(autoSubmitCountdown))
      : text;

  return (
    <Button
      id='complete__button'
      type='submit'
      buttonType={component.attributes['button-type'] ?? ButtonType.Primary}
      onClick={handleComplete}
      disabled={isDisabled}
      styles={component?.styles}
    >
      {buttonText}
    </Button>
  );
};

export default ButtonComplete;
